import { Segment } from '@dna/analytics'
import { RespondToViewport, WithProfiler } from '@dna/mixins'
import * as paths from '@dna/paths'
import { storefrontClient } from '@dna/storefront'
import styles from '@dna/styles'
import {
  addToCart,
  getLineItemProperties,
  getMasterSku,
  igGetExperimentControl,
  igGetExperimentVariationName,
  igReady,
  renderPrice,
  splitProductName,
  t,
  updateCartQuantity,
} from '@dna/utils'

import {
  LitElement,
  classMap,
  createRef,
  css,
  html,
  nothing,
  ref,
  repeat,
  styleMap,
  unsafeHTML,
  when,
} from '@lit'

class DNAMiniCart extends WithProfiler(RespondToViewport(LitElement)) {
  static properties = {
    initialItems: {
      type: Array,
    },
    items: {
      type: Array,
      state: true,
    },
    translations: {
      type: Object,
    },
    meta: {
      type: Object,
    },
    itemLimit: {
      type: Number,
    },
    cartItemsExpanded: {
      type: Boolean,
      state: true,
    },
    pendingEmptyCart: {
      type: Boolean,
    },
    savedItems: {
      type: Array,
      state: true,
    },
    tech1784Variant: {
      type: String,
      state: true,
    },
  }

  get subtotal() {
    return this.items.reduce(
      (acc, current) =>
        acc + Number.parseFloat(current.final_price) * current.quantity,
      0,
    )
  }

  get formattedSubtotal() {
    const renderedPrice = renderPrice(this.subtotal)
    return renderedPrice
  }

  get originalPriceTotal() {
    const originalPrice = this.items.reduce(
      (acc, current) =>
        acc + Number.parseFloat(current.original_price) * current.quantity,
      0,
    )
    return originalPrice
  }

  get estimatedTotal() {
    const shippingPrice =
      this.shippingValue !== 'FREE' ? this.meta?.shipping?.charge * 100 : 0

    return this.originalPriceTotal - this.totalSavings + shippingPrice
  }

  get formattedEstimatedTotal() {
    const renderedPrice = renderPrice(this.estimatedTotal)
    return renderedPrice
  }

  get orderDiscount() {
    let totalOrderDiscount = 0
    for (const discount of this.meta.cart_level_discount_applications) {
      totalOrderDiscount =
        totalOrderDiscount + Number(discount?.total_allocated_amount || 0)
    }
    return totalOrderDiscount
  }

  get totalSavings() {
    const orderDiscount = this.orderDiscount
    return this.originalPriceTotal - this.subtotal + orderDiscount
  }

  get formattedTotalSavings() {
    const renderedPrice = renderPrice(this.totalSavings)
    return renderedPrice
  }

  get formattedShipping() {
    const renderedPrice = renderPrice((this.meta?.shipping?.charge || 0) * 100)
    return renderedPrice
  }

  get enableUpsell() {
    return (
      (window.Shopify?.country !== 'US' && this.meta?.enable_upsell) || false
    )
  }

  get enableSaveForLater() {
    return this.meta?.enable_save_for_later || false
  }

  get isShippingOverThreshold() {
    return (
      this.meta.shipping.isFree || this.subtotal >= this.meta.shipping.threshold
    )
  }

  get shippingBar() {
    if (!this.meta) {
      return {
        current: 0,
        remaining: 0,
        remainingFormatted: 0,
        threshold: 0,
      }
    }

    const threshold = (this.meta.shipping.threshold || 0) * 100
    const remaining = threshold - this.subtotal

    const renderedPrice = renderPrice(remaining)
    return {
      current: (this.subtotal / threshold) * 100,
      remaining: remaining < 0 ? 0 : remaining,
      remainingFormatted: remaining < 0 ? 0 : renderedPrice,
      threshold: threshold,
    }
  }

  get shippingValue() {
    const freeShippingValue = this.translations.shipping_amount_free || 'FREE'
    if (this.meta.shipping.isFree) {
      return freeShippingValue
    }

    const shippingThreshold = this.meta.shipping.threshold * 100

    if (this.subtotal < shippingThreshold) {
      return this.formattedShipping
    }

    return freeShippingValue
  }

  get count() {
    const count = this.items.reduce(
      (acc, current) => acc + Number.parseInt(current.quantity),
      0,
    )
    return count
  }

  get showPaymentIcons() {
    // @intelligems TECH-1784 - V1 hides the payment icons, V2 shows them
    if (this.tech1784Variant === 'v1') {
      return false
    }
    if (this.tech1784Variant === 'v2') {
      return this.meta?.show_payment_icons || false
    }
    return (
      (window.Shopify?.country === 'US' && this.meta?.show_payment_icons) ||
      false
    )
  }

  get showAcceleratedCheckout() {
    return (
      (this.isMobile
        ? this.meta?.show_accelerated_checkout_mobile
        : this.meta?.show_accelerated_checkout_desktop) ||
      this.tech1784Variant === 'v1' ||
      this.tech1784Variant === 'v2'
    )
  }

  get paymentIcons() {
    const platform = navigator.platform
    const userAgent = navigator.userAgent
    // Check if using iOS. Newer iPads do not always report as 'iPad', so we need to check platform + touchpoints (touchscreen)
    const isIOS =
      userAgent.includes('iPhone') ||
      userAgent.includes('iPad') ||
      (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
    // On Mac, browsers have both 'Safari' and that browser in the user agent name so we need to ensure we are not on any of those browsers
    const isMacSafari =
      platform === 'MacIntel' &&
      userAgent.includes('Safari') &&
      !userAgent.includes('Chrome') &&
      !userAgent.includes('Edg') &&
      !userAgent.includes('FireFox')

    if (!isIOS && !isMacSafari) {
      return {
        src: this.meta?.payment_icons_other,
        alt: this.meta?.payment_icons_other_alt,
      }
    }
    return { src: this.meta?.payment_icons, alt: this.meta?.payment_icons_alt }
  }

  get storedSavedForLater() {
    const savedForLaterObj = localStorage.getItem('rothys_saved_for_later')
    if (!savedForLaterObj) {
      return { market: null, items: [] }
    }

    return JSON.parse(savedForLaterObj)
  }

  $cartDrawer = createRef()

  constructor() {
    super()
    this.initialItems = []
    this.items = []
    this.translations = {}
    this.itemLimit = 3
    this.savedItems = []

    this.cartItemsExpanded = false
    this.pendingEmptyCart = false
    this.tech1784Variant = 'control'
    this._hypeRefreshCount = 0
  }

  connectedCallback() {
    super.connectedCallback()
    document.addEventListener('dna:cart:open', this._handleOpenCart)
    document.addEventListener('dna:cart:close', this._handleCloseCart)
    document.addEventListener('dna:cart:refresh', this.refresh)
    window.addEventListener('pageshow', this._onPageShow)
    window.addEventListener(
      'dna-pending-empty-cart',
      this._handlePendingEmptyCart,
    )
    document.addEventListener(
      'dna:cart:save-for-later',
      this._handleSaveForLater,
    )
    document.addEventListener('dna:cart:move-to-cart', this._handleMoveToCart)

    igReady.then(async () => {
      // @intelligems TECH-1784
      const tech1784VariationName = await igGetExperimentVariationName({
        staging: '9bb4075d-0149-480d-bfbb-bc881f344ae1',
        production: '53d90909-30d9-4885-b58f-674d0282845e',
      })
      if (tech1784VariationName?.toLowerCase().includes('v1')) {
        this.tech1784Variant = 'v1'
      } else if (tech1784VariationName?.toLowerCase().includes('v2')) {
        this.tech1784Variant = 'v2'
      }
      // END / TECH-1784
    })

    document.addEventListener(
      'hype:free-gifts-added',
      this._handleHypeGiftsChange,
    )
    document.addEventListener(
      'hype:free-gifts-removed',
      this._handleHypeGiftsChange,
    )
  }
  disconnectedCallback() {
    super.disconnectedCallback()
    document.removeEventListener('dna:cart:open', this._handleOpenCart)
    document.removeEventListener('dna:cart:close', this._handleCloseCart)
    document.removeEventListener('dna:cart:refresh', this.refresh)
    window.removeEventListener('pageshow', this._onPageShow)
    window.removeEventListener(
      'dna-pending-empty-cart',
      this._handlePendingEmptyCart,
    )
    document.removeEventListener(
      'dna:cart:save-for-later',
      this._handleSaveForLater,
    )
    document.removeEventListener(
      'dna:cart:move-to-cart',
      this._handleMoveToCart,
    )

    document.removeEventListener(
      'hype:free-gifts-added',
      this._handleHypeGiftsChange,
    )
    document.removeEventListener(
      'hype:free-gifts-removed',
      this._handleHypeGiftsChange,
    )
  }

  willUpdate(changedProperties) {
    if (changedProperties.has('initialItems')) {
      this.items = this._normalizeCartItemsData(this.initialItems)
    }

    if (changedProperties.has('items')) {
      document.dispatchEvent(
        new CustomEvent('dna:cart:items-count', {
          detail: { count: this.count },
        }),
      )
    }

    if (changedProperties.has('meta')) {
      this._loadSavedItems()
    }
  }

  _onPageShow = (event) => {
    if (event.persisted) {
      this.refresh()
    }
  }

  _normalizeCartItemsData(items) {
    /**
     * We remove all zero quantity items from the cart
     * The Status wishlist is adding items with quantity 0
     * TODO: This needs to be handled by Status App.
     * https://rothy-s.atlassian.net/browse/TECH-640
     */
    const filteredItems = items.filter((item) => item.quantity > 0)
    /**
     * We are getting the total quantity based on item id
     * If cart items have different properties or discounts, they will show as separate line items
     * We want to update the maxOrdered amount based on item id in cart, not line item
     */
    const itemQuantities = filteredItems.reduce((acc, item) => {
      acc[item.id] = (acc[item.id] || 0) + Number(item.quantity)
      return acc
    }, {})
    /*
     * We need to account for Shopify appending tenths/hundredths
     * to currencies without minor units.
     * https://shopify.dev/docs/api/liquid/objects/line_item#line_item-original_price
     */
    return filteredItems.map((item) => ({
      ...item,
      original_price: item.original_price,
      final_price: item.final_price,
      discounts:
        item.line_level_discount_allocations?.map((discount) => ({
          title: discount.discount_application?.title || '',
          amount: Number(discount.amount),
        })) || [],
      max_ordered: this.isLimitExceeded(
        itemQuantities[item.id],
        item?.properties?._limit,
      ),
    }))
  }

  isLimitExceeded(quantity, limit) {
    this.itemLimit = this.meta.global_limit
      ? limit || this.meta.global_limit
      : quantity + 1

    return quantity >= this.itemLimit
  }

  _handleOpenCart = () => {
    document.dispatchEvent(new CustomEvent('dna:search-bar:close'))
    customElements
      .whenDefined('sl-drawer')
      .then(() => this.$cartDrawer.value.show())
    this._trackCartView()
  }

  _handleCloseCart = () => {
    this.$cartDrawer.value.hide()
  }

  _handleDrawerAfterHide = () => {
    document
      .querySelector('dna-cart-toggle')
      ?.shadowRoot?.querySelector('sl-button')
      .focus()
  }

  _handlePendingEmptyCart = () => {
    // there is a slight delay when we remove the last item from cart and items.length is empty.
    // To make a less choppy UX experience, we will having a pending state before all items are removed.
    if (this.items.length === 1) {
      this.pendingEmptyCart = true
    }
  }

  _handlePendingUndoItems = async () => {
    // When we close the drawer or click 'Checkout', we want to remove all items in 'undo' state,regardless of undo timeout
    const cartItems = this.shadowRoot.querySelectorAll('dna-cart-item')
    for (const cartItem of cartItems) {
      await cartItem._handleRemoveItem?.()
    }
  }

  _handleSaveForLater = async (event) => {
    if (!this.enableSaveForLater) return

    const item = event.detail.item
    // Handles the case of updating quantity within Saved for Later section
    const existingSavedItem = this.savedItems.find(
      (savedItem) => savedItem.variant_id === item.variant_id,
    )

    let mergedItem = item
    if (existingSavedItem && event.detail.mergeWithExisting) {
      const productLimit =
        Number(item.properties?._limit) || Number(this.meta?.global_limit)
      const totalQuantity =
        (existingSavedItem.quantity || 1) + (item.quantity || 1)
      const quantityLimit =
        productLimit && totalQuantity > productLimit
          ? productLimit
          : totalQuantity

      mergedItem = {
        ...existingSavedItem,
        ...item,
        quantity: quantityLimit,
        max_ordered: productLimit ? quantityLimit >= productLimit : false,
      }
    }

    if (item.quantity > 0) {
      if (existingSavedItem) {
        this.savedItems = this.savedItems.map((savedItem) =>
          savedItem.variant_id === item.variant_id ? mergedItem : savedItem,
        )
      } else {
        this.savedItems = [...this.savedItems, mergedItem]
      }
    } else {
      this.savedItems = this.savedItems.filter(
        (savedItem) => savedItem.variant_id !== item.variant_id,
      )
    }

    try {
      localStorage.setItem(
        'rothys_saved_for_later',
        JSON.stringify({
          market: window.Shopify?.country || null,
          items: this.savedItems,
        }),
      )
    } catch (error) {
      console.warn('Unable to save items to localStorage', error)
    }

    if (event.detail.skipCartUpdate) {
      return
    }

    await updateCartQuantity(
      [
        {
          id: item.key,
          quantity: 0,
        },
      ],
      false,
    )
    await this.refresh()
  }

  _handleMoveToCart = async (event) => {
    if (!this.enableSaveForLater) return

    const item = event.detail.item
    const savedQuantity = item.quantity || 1
    const variantId = item.variant_id

    const matchingCartItems = this.items.filter(
      (item) => (item.variant_id || item.id) === variantId,
    )
    let currentCartQuantity = 0

    for (const cartItem of matchingCartItems) {
      currentCartQuantity = currentCartQuantity + Number(cartItem.quantity)
    }

    const productLimit = item.properties?._limit || this.meta?.global_limit

    const totalQuantity = currentCartQuantity + savedQuantity
    const quantityToUse =
      totalQuantity > productLimit ? productLimit : totalQuantity

    const quantityToAdd = quantityToUse - currentCartQuantity

    if (quantityToAdd > 0) {
      if (matchingCartItems.length > 0) {
        const updates = [
          {
            id: matchingCartItems[0].key,
            quantity: quantityToUse,
          },
        ]

        for (const item of matchingCartItems.slice(1)) {
          updates.push({
            id: item.key,
            quantity: 0,
          })
        }

        await updateCartQuantity(updates, false)
      } else {
        await addToCart(
          {
            id: variantId,
            quantity: quantityToAdd,
            properties: item.properties,
          },
          false,
        )
      }
    }

    // Remove item that is being moved to cart from the saved items and set the value
    this.savedItems = this.savedItems.filter(
      (savedItem) => savedItem.variant_id !== item.variant_id,
    )

    try {
      localStorage.setItem(
        'rothys_saved_for_later',
        JSON.stringify({
          market: window.Shopify?.country || null,
          items: this.savedItems,
        }),
      )
    } catch (error) {
      console.warn('Unable to save items to localStorage', error)
    }

    await this.refresh()
  }

  _handleHypeGiftsChange = () => {
    this._hypeRefreshCount++
    this.refresh()
  }

  _loadSavedItems = async () => {
    if (!this.enableSaveForLater) {
      this.savedItems = []
      return
    }

    const { market, items } = this.storedSavedForLater
    if (items?.length && market !== window.Shopify?.country) {
      this.savedItems = await this._refreshSavedForLaterPrices(items)
    } else {
      this.savedItems = items
    }
  }

  // Since saved items are from local storage, they do not update when market changes
  // We want to fetch updated information for the product once you change markets
  _refreshSavedForLaterPrices = async (items) => {
    if (!items.length) return items

    const updatedItems = []

    for (const item of items) {
      let updatedItem = item
      try {
        const variant = await storefrontClient.getVariantById(item.variant_id)
        if (variant) {
          updatedItem = {
            ...item,
            final_price: variant.price,
            original_price: variant.price,
            url: variant.handle
              ? `${window.Shopify.routes.root}products/${variant.handle}`
              : item.url,
            properties: {
              ...item.properties,
              _compare_at_price: variant.compareAtPrice,
            },
          }
        }
      } catch (error) {
        console.error('Error refreshing save for later item', error)
      }
      updatedItems.push(updatedItem)
    }

    try {
      localStorage.setItem(
        'rothys_saved_for_later',
        JSON.stringify({
          market: window.Shopify?.country || null,
          items: updatedItems,
        }),
      )
    } catch (error) {
      console.warn('Unable to update saved for later items', error)
    }
    return updatedItems
  }

  _toggleMiniCartExpand = () => {
    this.cartItemsExpanded = !this.cartItemsExpanded
  }

  _trackCartView = () => {
    // @segment
    Segment.protect(() => {
      const analyticsData = {
        cart_id: Segment.cartToken,
      }
      analyticsData.products = this.items.map((item) => {
        return {
          brand: Segment.CONSTANTS.BRAND,
          category: splitProductName(item.product_title).style,
          currency: Segment.presentmentCurrency,
          image_url: item.image,
          name: splitProductName(item.product_title).color,
          price: Number(item.final_price / 100),
          product_category: item.properties._storefront_category,
          product_id: item.id.toString(),
          quantity: Number(item.quantity),
          shopify_product_id: item.product_id.toString(),
          shopify_variant_id: item.variant_id.toString(),
          sku: item.sku,
          sku_short: getMasterSku(item.sku),
          url: `${window.location.origin}${item.url}`,
          variant: item.variant_title,
        }
      })
      Segment.track(Segment.CONSTANTS.EVENTS.CART_VIEWED, analyticsData)
    })
  }

  _trackCartEmptyClicks = (event) => {
    // @segment
    Segment.protect(() => {
      const $clickedLink = event.target?.closest('a')
      if ($clickedLink) {
        Segment.track(Segment.CONSTANTS.EVENTS.CART_EMPTY_CONTENT_CLICKED, {
          empty_cart_link: $clickedLink.href,
          empty_cart_link_title: $clickedLink.textContent?.trim(),
        })
      }
    })
  }

  _trackAcceleratedCheckoutClick = (event) => {
    // @segment
    Segment.protect(() => {
      const walletButtonSelector =
        'shop-pay-wallet-button, shopify-amazon-pay-button, shopify-apple-pay-button, shopify-google-pay-button, shopify-paypal-button'
      const path = event.composedPath?.() || []
      const $clickedButton =
        path.find((node) => node?.matches?.(walletButtonSelector)) ||
        event.target?.closest?.(walletButtonSelector)

      // Ignore clicks on the gap between buttons, including button outlines that overflow into it
      if (!$clickedButton) return

      let acceleratedButtonId = 'Unidentified'

      if ($clickedButton.matches('shop-pay-wallet-button')) {
        acceleratedButtonId = 'Shop Pay'
      } else if ($clickedButton.matches('shopify-amazon-pay-button')) {
        acceleratedButtonId = 'Amazon Pay'
      } else if ($clickedButton.matches('shopify-apple-pay-button')) {
        acceleratedButtonId = 'Apple Pay'
      } else if ($clickedButton.matches('shopify-google-pay-button')) {
        acceleratedButtonId = 'Google Pay'
      } else if ($clickedButton.matches('shopify-paypal-button')) {
        acceleratedButtonId = 'PayPal'
      }
      Segment.track(Segment.CONSTANTS.EVENTS.EXPRESS_CHECKOUT_INITIATED, {
        express_checkout_type: acceleratedButtonId,
        express_checkout_context: 'mini_cart',
      })
    })
  }

  _trackCheckoutButtonClick = async (event) => {
    event.preventDefault()
    try {
      await this._handlePendingUndoItems()
    } catch (error) {
      console.error(error)
    }

    // @segment
    Segment.protect(() => {
      Segment.track(Segment.CONSTANTS.EVENTS.CART_CHECKOUT_BUTTON_CLICKED)
    })
    window.location.href = event.target.getAttribute('href')
  }

  refresh = async (event) => {
    const hypeRefreshCount = this._hypeRefreshCount
    let newItems
    let cartDiscount
    if (event?.detail?.token) {
      cartDiscount = event?.detail?.cart_level_discount_applications
      newItems = event?.detail?.items || []

      for (const item of newItems) {
        // The cart update response doesn't provide latest line item data
        const [lineItemProduct, lineItemVariant] = await Promise.all([
          storefrontClient.getProductById(item.product_id),
          storefrontClient.getVariantById(item.variant_id),
        ])
        const currentProps = item.properties || {}
        const newProps = getLineItemProperties(lineItemProduct, lineItemVariant)
        item.properties = {
          ...currentProps,
          ...newProps,
        }
      }
    } else {
      // Otherwise fetch required cart data
      try {
        const response = await fetch(`${window.Shopify.routes.root}cart.js`)
        if (!response.ok) {
          throw new Error('Error fetching cart')
        }
        const data = await response.json()
        newItems = data.items
        cartDiscount = data.cart_level_discount_applications
      } catch {
        console.error('Error fetching cart')
      }
    }
    // For Hype GWP Promos to prevent cart from being updated with stale data
    if (hypeRefreshCount !== this._hypeRefreshCount) return

    this.items = this._normalizeCartItemsData(newItems)
    if (this.items.length > 0) {
      this.pendingEmptyCart = false
    }
    this.meta.cart_level_discount_applications = cartDiscount.map(
      (item) => item?.discount_application || item,
    )
  }

  _renderEmptyState = () => {
    const showSavedForLaterEmptyState =
      this.enableSaveForLater && this.savedItems.length > 0

    return html`
      <div
        class="empty-state ${showSavedForLaterEmptyState ? 'empty-state--save-for-later' : ''}"
        @click="${this._trackCartEmptyClicks}">
        ${
          showSavedForLaterEmptyState
            ? html`
            <div class="cart-empty">
              <p class="p1 cart-empty__msg">
                ${unsafeHTML(this.translations.empty_with_saved_for_later)}
              </p>
            </div>
          `
            : html`<slot name="empty-state"></slot>`
        }
      </div>
      ${this._renderSavedForLater()}
    `
  }

  _renderPaymentIcons = () => {
    const { src, alt } = this.paymentIcons
    return html`
      <div class="breakdown__payment-methods js-breakdown-payment-icons">
        <div class="breakdown__payment-methods-title">${this.translations.payment_methods}</div>
        <div class="breakdown__payment-methods-icons">
          <img
            src="${src}"
            alt="${alt}"
            width="300"
            loading="lazy"
          />
        </div>
      </div>
    `
  }

  _renderItems = () => {
    const hasGiftCard = !!this.items.find(
      (item) => item.product_type === 'Virtual Gift Cards',
    )
    const count = this.items.length
    return html`
      ${when(
        this.meta?.enable_promo_message,
        this._renderPromoMessage,
        () => nothing,
      )}
      <div class="cart-drawer__items">
        ${repeat(
          this.items,
          (item) => item.key,
          (item) => html`
            <dna-cart-item
              @dna-change=${this.refresh}
              .item=${item}
              .finalSaleTooltip=${this.translations.final_sale_tooptip}
              .preorder=${this.translations.preorder}
              .backorder=${this.translations.backorder}
              .comingSoon=${this.translations.coming_soon}
              .estimatedShipDate=${this.translations.estimated_ship_date}
              .maximumAdded=${this.translations.maximum_added}
              .quantityOutOfStock=${this.translations.quantity_out_of_stock}
              .orderDiscount=${this.orderDiscount}
              .itemLimit=${this.itemLimit}
              .enableUndo=${this.meta?.enable_undo}
              .undoTime=${this.meta?.undo_time}
              .enableSaveForLater=${this.enableSaveForLater}
            ></dna-cart-item>
          `,
        )}
      </div>
      ${when(
        this.enableUpsell,
        () => html`
        <dna-mini-cart-upsell
        .title=${this.translations.upsell_section_title}
        .items=${this.items}
      ></dna-mini-cart-upsell>
      `,
        () => nothing,
      )}

      <div class="breakdown">
        <div class="breakdown__prices">
          <p class="summary__total p2">
            <span>${this.translations.subtotal || 'Subtotal'}</span>
            <span>${this.formattedSubtotal}</span>
          </p>
          ${when(
            this.orderDiscount > 0,
            () => html`
            <p class="summary__total p2">${this.translations.order_discount || 'Order Discount'}</p>
            <div class="summary__total-discount">
              ${repeat(
                this.meta.cart_level_discount_applications,
                (discount) => discount,
                (discount) => html`
                  ${paths.discountTag}
                  <span class="discount-title"><strong>${discount.title}<strong></span>
                  <span class="discount-amount"><strong>-${renderPrice(discount.total_allocated_amount)}</strong></span>
                `,
              )}
            </div>
            `,
            () => nothing,
          )}
          <p class="summary__total p2">
            <span>${this.translations.shipping || 'Shipping'}</span>
            <span>${this.shippingValue}</span>
          </p>
          ${when(
            this.meta.tax !== '',
            () => html`
              <p class="summary__total p2">
                <span>${this.translations.taxes || 'Taxes'}</span>
                <span>${this.meta.tax}</span>
              </p>
            `,
            () => nothing,
          )}
          ${when(
            this.totalSavings > 0,
            () => html`
            <p class="summary__total summary__total-savings p2">
                ${paths.discountTag}
                <span class="discount-title"><strong>${this.translations.total_savings || 'Total Savings'}</strong></span>
                <span class="discount-amount"><strong>${this.formattedTotalSavings}</strong></span>
              </p>
              `,
            () => nothing,
          )}
        </div>
        ${when(
          this.showPaymentIcons,
          () => this._renderPaymentIcons(),
          () => nothing,
        )}
      </div>
      ${this._renderSavedForLater()}
      <div slot="footer" class="summary ${this.cartItemsExpanded ? 'hidden' : ''}">
        <div class="summary__content">
          <div class="summary__bottom">
            <p class="summary__total p2">
              <strong>${this.translations.estimated_total || 'Estimated Total'}</strong>
              <strong>
                ${when(
                  window.Shopify.currency.active !== 'USD',
                  () => html`${window.Shopify.currency.active} `,
                  () => nothing,
                )}
              <strong>${this.formattedEstimatedTotal}</strong>
              </strong>
            </p>
            <slot name="checkout-button">
              <!-- 
                Ensure to override this default slot content if you need a button
                that 3rd party apps can access via light DOM.
              -->
              <sl-button
                href='${window.Shopify.routes.root}checkout'
                variant="primary"
                @click=${this._trackCheckoutButtonClick}
              >
              ${this.translations.checkout_button}
              </sl-button>
            </slot>
            ${when(
              this.showAcceleratedCheckout,
              () => html`
                <div
                  class="accelerated-checkout"
                  @click=${this._trackAcceleratedCheckoutClick}
                >
                  <slot name="accelerated-checkout"></slot>
                </div>
              `,
              () => nothing,
            )}
            ${when(
              this.meta?.shipping?.has_final_line,
              this._renderShippingTerms,
              () => nothing,
            )}
          </div>
        </div>
      </div>
    `
  }

  _renderShippingMessage = () => {
    const shippingMessage = t('cart.general.free_shipping_unlocked')
    const updatedShippingText = document.createElement('textarea')
    updatedShippingText.innerHTML = shippingMessage
    return html`
      <div class="shipping-message">
        ${when(
          this.shippingBar?.remaining <= 0,
          () => html`
            <p class="p1 shipping-message__text shipping-congrats">
              ${paths.checkMark}
              ${unsafeHTML(updatedShippingText.value)}
            </p>
          `,
          () => html`
            <div>
              <p class="p1 shipping-message__text">
                <strong>
                  Add ${this.shippingBar?.remainingFormatted}
                </strong>
                to Receive
                <strong> Free Shipping!</strong>
              </p>
            </div>
          `,
        )}
        <div class="shipping-progress">
          <span
            class="shipping-progress__bar"
            style=${styleMap({
              width: `${this.shippingBar?.current}%`,
            })}
          ></span>
        </div>
        <p></p>
      </div>
    `
  }

  _renderPromoMessage = () => {
    return html`
      <div class="cart-promo">
        <div class="cart-promo-message">${this.translations.promo_message}</div>
      </div>
    `
  }

  _renderShippingTerms = () => {
    return html`
      <p class="summary__terms">
        <slot name="final-line"></slot>
      </p>
    `
  }

  _renderHypeProgressBar = () => {
    return html`<dna-progress-bar banner-id=${this.meta?.hype_banner_id} banner-text=${this.meta?.hype_banner_text}></dna-progress-bar>`
  }

  _renderSavedForLater = () => {
    if (!this.enableSaveForLater || !this.savedItems.length) return nothing
    return html`
    <div class="cart-drawer__saved">
      <p class="cart-drawer__saved-title">
        ${this.translations.saved_for_later || t('cart.general.saved_for_later')}
      </p>
      <div class="cart-drawer__saved-items">
        ${repeat(
          this.savedItems,
          (item) => item.variant_id,
          (item) => html`
            <dna-cart-item
              isSaveForLater
              .item=${item}
              .finalSaleTooltip=${this.translations.final_sale_tooptip}
              .preorder=${this.translations.preorder}
              .backorder=${this.translations.backorder}
              .comingSoon=${this.translations.coming_soon}
              .estimatedShipDate=${this.translations.estimated_ship_date}
              .maximumAdded=${this.translations.maximum_added}
              .quantityOutOfStock=${this.translations.quantity_out_of_stock}
              .itemLimit=${this.itemLimit}
              .enableUndo=${false}
            ></dna-cart-item>
          `,
        )}
      </div>
    </div>`
  }

  render() {
    return html`
      <sl-drawer
        label="My Cart"
        class=${classMap({
          drawer: true,
          'cart--empty': this.count === 0,
          'tech1784-variant': this.showAcceleratedCheckout,
        })}
        @sl-after-hide=${this._handleDrawerAfterHide}
        ${ref(this.$cartDrawer)}
        @sl-hide=${this._handlePendingUndoItems}
      >
        <div slot="label">
          <button aria-label=${this.translations.expand_accessible_message} aria-expanded=${this.cartItemsExpanded} class="drawer__expand" @click=${this._toggleMiniCartExpand}>
            <span class="${this.cartItemsExpanded ? 'hidden' : ''}">${paths.hamburger}</span>
            <span class="${this.cartItemsExpanded ? '' : 'hidden'}">${paths.close}</span>
          </button>
          <p class="h4 drawer__label">
            ${this.translations.cart_title}
            ${when(
              this.count > 0,
              () => html`
                <span class="badge">
                  (${this.count}<span class="sr-only">${this.count > 1 ? this.translations.cart_count.other : this.translations.cart_count.one}</span>)
                </span>
              `,
              () => nothing,
            )}
          </p>
        </div>
        <div slot="header-actions" class="header-actions-child">
          ${when(
            this.meta?.shipping?.isFree || this.count === 0,
            () => nothing,
            this._renderShippingMessage,
          )}
        </div>
        ${when(this.meta?.hype_banner_id, this._renderHypeProgressBar, () => nothing)}
        ${when(
          !this.items.length || this.pendingEmptyCart,
          this._renderEmptyState,
          () => html`${this._renderItems()}`,
        )}
      </sl-drawer>
    `
  }

  static styles = [
    styles.a11y,
    styles.flex,
    styles.typography,
    styles.shoelace,
    css`
      :host {
        display: block;
      }

      sl-drawer:not(:defined) {
         display: none;
      }

      .drawer {
        --size: 100%;
      }
      /*
       * iOS Safari sizes 100%/100vh against the *large* (toolbar-hidden)
       * viewport, so the drawer's bottom footer (checkout CTA) gets pushed
       * beneath the visible area as the browser chrome collapses/expands.
       * Pin the panel to the dynamic viewport so the footer stays in view.
       * 100vh is the fallback for browsers without dvh support.
       */
      .drawer::part(panel) {
        height: 100vh;
        height: 100dvh;
        max-height: 100vh;
        max-height: 100dvh;
      }
      .drawer::part(overlay) {
        fill: var(--dna-utility-overlay, rgba(0, 0, 0, 0.5));
      }
      .drawer::part(header) {
        align-items: baseline;
        display: grid;
        place-items: center;
      }
      .drawer::part(title) {
        text-align: center;
        padding-top: 32px;
        padding-bottom: 16px;
        line-height: 1em;
      }
      .drawer::part(header-actions) {
        width: 100%;
        display: flex;
        justify-content: center;
      }
      .cart-drawer__items {
        border-top: 1px solid var(--dna-color-light-grey);
      }
      .drawer [slot="label"] .summary__terms {
        margin-bottom: 0;
      }
      .cart--empty.drawer::part(header-actions) {
        border-bottom: none;
      }
      .header-actions-child {
        width: 100%;
      }
      .drawer__label {
        margin: 0;
      }
      @media screen and (max-width: 768px) {
        .drawer__label {
         font-size: 32px;
        }
        .drawer__label .badge {
          font-size: 14px;
        }
      }
      .drawer__expand {
        display: none;
      }
      @media (min-width: 300px) and (max-width: 700px) and (min-height: 150px) and (max-height: 400px) {
        .drawer__expand {
          left: 50px;
          position: absolute;
          height: 36px;
          width: 36px;
          display: block;
          color: var(--dna-color-midnight);
          background-color: transparent;
          border: none;
          cursor: pointer;
        }
      }
      .drawer::part(body) {
        padding: 0;
        display: flex;
        flex-direction: column;
      }
      .drawer::part(close-button) {
        cursor: pointer;
        height: calc(var(--dna-space-6) + var(--dna-space-5));
        width: calc(var(--dna-space-6) + var(--dna-space-5));
        position: absolute;
        top: var(--dna-space-3);
        right: var(--dna-space-3);
      }
      .drawer::part(close-button__base) {
        color: black;
        width: var(--dna-space-6);
        height: var(--dna-space-6);
        font-size: var(--dna-space-6);
        padding: 0;
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
      }
      .drawer::part(footer) {
        padding: 0 0 env(safe-area-inset-bottom, 0px) 0;
      }
      .empty-state {
        display: flex;
        flex-direction: column;
        justify-content: center;
        align-items: center;
        height: 100%;
        animation: fade-in 0.3s ease;
      }
      .empty-state--save-for-later {
        margin: var(--dna-space-4) var(--dna-space-10);
        text-align: center;
        height: auto;
      }
      @media screen and (min-width: 768px) {
        .empty-state--save-for-later {
          margin: var(--dna-space-10) 70px;
        }
      }
      .empty-state p {
        font-size: 14px;
      }
      sl-button::part(base) {
        background-color: #000;
        color: #fff;
        height: var(--dna-button-height);
        width: 150px;
        border: none;
        font-weight: 600;
      }
      .breakdown {
        padding: 0;
      }
      .summary {
        padding: 0;
      }
      .breakdown__prices {
        padding: 0 16px 24px 16px;
      }
      .breakdown__payment-methods {
        text-align: center;
        padding-bottom: 20px;
      }
      .breakdown__payment-methods-title {
        font-size: 12px;
        margin-bottom: 8px;
        line-height: 18px;
        letter-spacing: 0.5px;
        color: var(--dna-color-midnight);
      }
      .summary__content {
        padding: 0 16px 12px 16px;
        border-top: 0.5px solid var(--dna-color-light-grey);
      }
      @media screen and (max-width: 767px) {
        .drawer.tech1784-variant .summary__content {
          padding: 0 4px 12px 4px;
        }
      }
      /* breakpoint-tablet */
      @media screen and (min-width: 768px) {
        .drawer {
          --size: 488px;
        }
        .drawer.tech1784-variant {
          --size: 590px;
        }
        .drawer.tech1784-variant .summary__content {
          padding: 0 24px 12px 24px;
        }
        .drawer::part(title) {
          padding-top: 40px;
          padding-bottom: 17px;
        }
        .breakdown__prices {
          padding: 0 40px 16px 40px;
        }
        .summary__content {
          padding: 0 40px 16px 40px;
        }
        .breakdown .summary__total:first-of-type {
        margin-top: 32px;
        }
      }
      .summary__total {
        display: flex;
        justify-content: space-between;
        margin: 0;
        padding-top: 12px;
        padding-bottom: 12px;
        border-top: 0.5px solid var(--dna-color-light-grey);
      }
      .breakdown .summary__total:first-of-type {
        margin-top: 40px;
      }
      .breakdown .summary__total:last-of-type {
        border-bottom: 0.5px solid var(--dna-color-light-grey);
      }
      .summary__bottom .summary__total {
        border: none;
      }
      .summary__total-savings {
        justify-content: flex-start;
        align-items: center;
      }
      .summary__total-discount {
        display: flex;
        padding-top: 8px;
      }
      .discount-title {
        font: var(--dna-body-p2-font);
        margin-left: 4px;
        margin-right: 4px;
      }
      .discount-amount {
        font: var(--dna-body-p2-font);
        line-height: 12px;
      }
      .summary__total-discount .discount-amount {
        margin-left: auto;
      }
      .summary__terms {
        color: var(--dna-color-midnight, #808080);
        text-align: center;
        font-size: 12px;
        font-style: normal;
        font-weight: 400;
        line-height: 140%; /* 16.8px */
      }
      dna-cart-item {
        border-bottom: 1px solid var(--dna-color-light-grey);
      }
      dna-cart-item[showundo]:has(+ dna-cart-item[showundo]),
      dna-cart-item[showundo]:last-child {
        border-bottom: none;
        --undo-margin-bottom: 0;
      }
      .shipping-progress {
        background-color: var(--dna-color-sandstone);
        height: 4px;
        width: 100%;
        position: relative;
        overflow: hidden;
      }
      .shipping-progress__bar {
        background-color: var(--dna-brand-primary);
        height: 100%;
        display: block;
        position: absolute;
        top: 0;
        left: 0;
        transition: width 0.3s;
      }
      .shipping-congrats {
        display: flex;
        flex-flow: wrap;
        align-items: center;
        gap: var(--dna-space-1);
      }
      .shipping-message__text {
        font-size: 12px;
      }
      /* breakpoint-tablet */
      @media screen and (min-width: 768px) {
        .shipping-message__text {
          font-size: 14px;
        }
      }
      .shipping-congrats svg {
        padding-right: var(--dna-space-2);
      }
      .shipping-message {
        flex: 1;
        width: 100%;
      }
      ::slotted([slot='checkout-button']) {
        width: 100%;
      }
      .accelerated-checkout {
        margin-top: var(--dna-space-3, 12px);
        --shopify-accelerated-checkout-button-block-size: 40px;
        --shopify-accelerated-checkout-row-gap: var(--dna-space-2, 8px);
        --shopify-accelerated-checkout-button-border-radius: 0px;
        
      }
      .cart-promo {
        border-bottom: 1px solid var(--dna-color-light-grey);
      }
      .cart-promo-message {
        text-align: center;
        border: 1px solid var(--dna-color-light-grey);
        font-size: 14px;
        margin: 16px 24px;
        padding-top: 11px;
        padding-bottom: 11px;
      }
      @keyframes fade-in {
        from {
          opacity: 0;
        }

        to {
          opacity: 1;
        }
      }
      .cart-drawer__saved {
        font: var(--dna-label-m-font);
        margin-top: var(--dna-space-10);
      }

      .cart-drawer__saved-title {
        padding: 0 0 var(--dna-space-2) var(--dna-space-4);
        margin: 0px;
        border-bottom: 1px solid var(--dna-color-light-grey);
        line-height: 32px;
      }

      .cart-drawer__saved-items {
        display: flex;
        flex-direction: column;
      }
    `,
  ]
}

customElements.define('dna-mini-cart', DNAMiniCart)
